import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ShieldCheck, ClipboardCheck, LogOut, Bell, Menu, X,
  LayoutDashboard, Wallet, Banknote, FileBarChart2,
  CheckCheck, RefreshCw,
} from "lucide-react";
import API from "../services/api";

/* ── How long ago helper ─────────────────────────────────────── */
const timeAgo = (date) => {
  if (!date) return "";
  const seconds = Math.floor((Date.now() - new Date(date)) / 1000);
  if (seconds < 60)   return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400)return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

/* ══════════════════════════════════════════════════════════════
   NOTIFICATION BELL  (self-contained)
   ══════════════════════════════════════════════════════════════ */
const NotificationBell = () => {
  const navigate = useNavigate();

  const [open,        setOpen]        = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [lastFetched, setLastFetched] = useState(null);
  const [dismissed,   setDismissed]   = useState(() => {
    try { return JSON.parse(localStorage.getItem("notif_dismissed") || "false"); }
    catch { return false; }
  });
  const [counts, setCounts] = useState({ submitted: 0, audit: 0 });

  const ref = useRef(null);

  /* ── Fetch counts ─────────────────────────────────────────── */
  const fetchCounts = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    try {
      const [appsRes, auditRes] = await Promise.all([
        API.get("/admin/applications/count?status=SUBMITTED"),
        API.get("/admin/audit-logs/count"),
      ]);
      const newSubmitted = appsRes.data.count  || 0;
      const newAudit     = auditRes.data.count || 0;

      setCounts((prev) => {
        const changed =
          newSubmitted !== prev.submitted || newAudit !== prev.audit;
        if (changed && dismissed) {
          setDismissed(false);
          localStorage.removeItem("notif_dismissed");
        }
        return { submitted: newSubmitted, audit: newAudit };
      });

      setLastFetched(new Date());
    } catch (err) {
      console.error("Failed to fetch notification counts", err);
    } finally {
      setLoading(false);
    }
  }, [dismissed]);

  /* ── Poll every 30 s ──────────────────────────────────────── */
  useEffect(() => {
    fetchCounts();
    const id = setInterval(() => fetchCounts(), 30000);
    return () => clearInterval(id);
  }, [fetchCounts]);

  /* ── Close on outside click ───────────────────────────────── */
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ── Mark all read ────────────────────────────────────────── */
  const markAllRead = (e) => {
    e.stopPropagation();
    setDismissed(true);
    localStorage.setItem("notif_dismissed", "true");
  };

  /* ── Navigate + close ─────────────────────────────────────── */
  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  const total     = counts.submitted + counts.audit;
  const showBadge = total > 0 && !dismissed;

  const items = [
    {
      id:      "apps",
      Icon:    ShieldCheck,
      iconBg:  counts.submitted > 0 ? "bg-blue-100"    : "bg-gray-100",
      iconCol: counts.submitted > 0 ? "text-primary"   : "text-gray-400",
      title:   counts.submitted > 0
                 ? `${counts.submitted} pending application${counts.submitted !== 1 ? "s" : ""}`
                 : "No pending applications",
      sub:     counts.submitted > 0 ? "Awaiting your review" : "All caught up",
      badge:   counts.submitted || null,
      badgeCl: "bg-blue-100 text-blue-700",
      path:    "/admin-dashboard",
      active:  counts.submitted > 0,
    },
    {
      id:      "audit",
      Icon:    ClipboardCheck,
      iconBg:  counts.audit > 0 ? "bg-yellow-100"  : "bg-gray-100",
      iconCol: counts.audit > 0 ? "text-yellow-600": "text-gray-400",
      title:   counts.audit > 0
                 ? `${counts.audit} audit log entr${counts.audit !== 1 ? "ies" : "y"}`
                 : "No recent activity",
      sub:     counts.audit > 0 ? "System activity recorded" : "Nothing to report",
      badge:   counts.audit || null,
      badgeCl: "bg-yellow-100 text-yellow-700",
      path:    "/admin-dashboard/audit",
      active:  counts.audit > 0,
    },
  ];

  return (
    <div className="relative" ref={ref}>

      {/* Bell button */}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Notifications"
        aria-expanded={open}
        className={`relative w-9 h-9 flex items-center justify-center rounded-xl transition-colors duration-150
          ${open ? "bg-gray-700" : "hover:bg-gray-800"}`}
      >
        <Bell
          size={18}
          className={showBadge ? "text-yellow-300" : "text-gray-300"}
        />
        {/* Pulsing dot */}
        {showBadge && (
          <span className="absolute top-1.5 right-1.5 flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white text-gray-800 shadow-2xl rounded-2xl border border-gray-100 z-[999] overflow-hidden">

          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50">
            <div className="flex items-center gap-2">
              <Bell size={13} className="text-gray-500" />
              <span className="font-semibold text-sm text-gray-800">Notifications</span>
              {showBadge && (
                <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                  {total}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => { e.stopPropagation(); fetchCounts(true); }}
                title="Refresh"
                className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-400 hover:text-gray-700 transition"
              >
                <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
              </button>
              {showBadge && (
                <button
                  onClick={markAllRead}
                  title="Mark all as read"
                  className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-400 hover:text-green-600 transition"
                >
                  <CheckCheck size={13} />
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-400 hover:text-gray-700 transition"
              >
                <X size={13} />
              </button>
            </div>
          </div>

          {/* Notification items — each is a clickable button */}
          <div className="divide-y divide-gray-50">
            {items.map((item) => (
              <button
                key={item.id}
                onClick={() => item.active && go(item.path)}
                className={`w-full text-left px-4 py-3.5 flex items-start gap-3 transition-colors duration-150 group
                  ${item.active
                    ? "hover:bg-blue-50 cursor-pointer"
                    : "cursor-default"}`}
              >
                {/* Icon */}
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${item.iconBg}`}>
                  <item.Icon size={16} className={item.iconCol} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className={`text-sm font-medium leading-tight
                      ${item.active ? "text-gray-800" : "text-gray-400"}`}>
                      {item.title}
                    </p>
                    {item.badge && (
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none flex-shrink-0 ${item.badgeCl}`}>
                        {item.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">{item.sub}</p>
                  {item.active && (
                    <p className="text-[10px] text-primary font-semibold mt-1 group-hover:underline">
                      Click to view →
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Footer */}
          <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
            <span className="text-[10px] text-gray-400">
              {lastFetched ? `Updated ${timeAgo(lastFetched)}` : "Loading..."}
            </span>
            <button
              onClick={() => go("/admin-dashboard")}
              className="text-[11px] text-primary font-semibold hover:underline"
            >
              Go to dashboard →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   ADMIN NAVBAR
   ══════════════════════════════════════════════════════════════ */
const AdminNavbar = ({ onLogout }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const isActive = (path, exact = false) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);

  const linkClass = (path, exact = false) =>
    `relative flex items-center gap-1.5 text-sm font-medium transition-colors duration-150 py-1 border-b-2 ${
      isActive(path, exact)
        ? "border-yellow-300 text-yellow-300"
        : "border-transparent text-gray-300 hover:text-yellow-300 hover:border-yellow-300/50"
    }`;

  const navLinks = (
    <>
      <Link
        to="/admin-dashboard"
        end="true"
        onClick={() => setMobileOpen(false)}
        className={linkClass("/admin-dashboard", true)}
      >
        <LayoutDashboard size={15} /> Dashboard
      </Link>
      <Link
        to="/admin-dashboard/budget"
        onClick={() => setMobileOpen(false)}
        className={linkClass("/admin-dashboard/budget")}
      >
        <Wallet size={15} /> Budget
      </Link>
      <Link
        to="/admin-dashboard/disbursements"
        onClick={() => setMobileOpen(false)}
        className={linkClass("/admin-dashboard/disbursements")}
      >
        <Banknote size={15} /> Disbursements
      </Link>
      <Link
        to="/admin-dashboard/reports"
        onClick={() => setMobileOpen(false)}
        className={linkClass("/admin-dashboard/reports")}
      >
        <FileBarChart2 size={15} /> Reports
      </Link>
      <Link
        to="/admin-dashboard/audit"
        onClick={() => setMobileOpen(false)}
        className={linkClass("/admin-dashboard/audit")}
      >
        <ClipboardCheck size={15} /> Audit Logs
      </Link>
      <button
        onClick={onLogout}
        className="flex items-center gap-1.5 text-sm font-medium text-gray-300 hover:text-red-300 transition-colors duration-150"
      >
        <LogOut size={15} /> Logout
      </button>
    </>
  );

  return (
    <nav className="bg-gray-900 text-white px-4 md:px-6 h-16 flex items-center justify-between relative shadow-md border-b border-gray-800 flex-shrink-0">

      {/* Brand */}
      <div className="font-bold text-base tracking-wide flex items-center gap-2 flex-shrink-0">
        <div className="w-7 h-7 bg-primary rounded-lg flex items-center justify-center">
          <ShieldCheck size={15} />
        </div>
        <span className="hidden sm:inline text-sm">CDF Admin Panel</span>
      </div>

      {/* Desktop links */}
      <div className="hidden md:flex items-center gap-6 mx-6">
        {navLinks}
      </div>

      {/* Right: bell + mobile toggle */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <NotificationBell />
        <button
          className="md:hidden w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-800 transition-colors duration-150"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {/* Mobile dropdown */}
      {mobileOpen && (
        <div className="absolute top-16 left-0 w-full bg-gray-900 border-t border-gray-800 flex flex-col gap-1 p-4 md:hidden z-50 shadow-lg">
          {navLinks}
        </div>
      )}
    </nav>
  );
};

export default AdminNavbar;